import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { securityPolicyAPI, authAPI } from '../services/api';
import { removeToken } from '../utils/auth';
import './SecurityPolicies.css';

function SecurityPolicies() {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingPolicy, setEditingPolicy] = useState(null);
    const [userName, setUserName] = useState('');
    const [userRole, setUserRole] = useState(null);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        minimumRequiredPositiveSecurityChecksAbsolute: 0,
        minimumRequiredPositiveSecurityChecksRelative: 0,
        requireDoubleSideScan: false
    });

    useEffect(() => {
        loadUserInfo();
        loadPolicies();
    }, []);

    const loadUserInfo = async () => {
        try {
            const response = await authAPI.getCurrentUser();
            const user = response.data.data.user;
            setUserRole(user.role);
            setUserName(user.name);
        } catch (err) {
            console.error('Failed to load user info:', err);
        }
    };

    const loadPolicies = async () => {
        try {
            setLoading(true);
            const response = await securityPolicyAPI.getPolicies();
            setPolicies(response.data.data.policies || []);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load security policies');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        removeToken();
        navigate('/login');
    };

    const openCreateModal = () => {
        setEditingPolicy(null);
        setFormData({
            name: '',
            minimumRequiredPositiveSecurityChecksAbsolute: 0,
            minimumRequiredPositiveSecurityChecksRelative: 0,
            requireDoubleSideScan: false
        });
        setShowModal(true);
        setError('');
        setSuccess('');
    };

    const openEditModal = (policy) => {
        setEditingPolicy(policy);
        setFormData({
            name: policy.name,
            minimumRequiredPositiveSecurityChecksAbsolute: policy.minimumRequiredPositiveSecurityChecksAbsolute,
            minimumRequiredPositiveSecurityChecksRelative: policy.minimumRequiredPositiveSecurityChecksRelative,
            requireDoubleSideScan: Boolean(policy.requireDoubleSideScan) // Ensure it's a boolean
        });
        setShowModal(true);
        setError('');
        setSuccess('');
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingPolicy(null);
        setError('');
        setSuccess('');
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validation
        if (!formData.name.trim()) {
            setError('Name is required');
            return;
        }

        if (formData.minimumRequiredPositiveSecurityChecksAbsolute < 0) {
            setError('Minimum absolute checks must be 0 or greater');
            return;
        }

        if (formData.minimumRequiredPositiveSecurityChecksRelative < 0 ||
            formData.minimumRequiredPositiveSecurityChecksRelative > 100) {
            setError('Minimum relative checks must be between 0 and 100');
            return;
        }

        try {
            if (editingPolicy) {
                await securityPolicyAPI.updatePolicy(editingPolicy.id, formData);
                setSuccess('Security policy updated successfully');
            } else {
                await securityPolicyAPI.createPolicy(formData);
                setSuccess('Security policy created successfully');
            }

            setTimeout(() => {
                closeModal();
                loadPolicies();
            }, 1000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save security policy');
        }
    };

    const handleDelete = async (policy) => {
        if (!window.confirm(`Are you sure you want to delete security policy "${policy.name}"?`)) {
            return;
        }

        try {
            await securityPolicyAPI.deletePolicy(policy.id);
            setSuccess('Security policy deleted successfully');
            setTimeout(() => setSuccess(''), 3000);
            loadPolicies();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete security policy');
            setTimeout(() => setError(''), 3000);
        }
    };

    return (
        <div className="security-policies-page">
            <header className="page-header">
                <div>
                    <h1>Security Policies</h1>
                    {userName && (
                        <p className="user-info">
                            {userName} {userRole && <span className="badge">{userRole}</span>}
                        </p>
                    )}
                </div>
                <div className="header-actions">
                    <button onClick={() => navigate('/')} className="secondary-btn">
                        Back to Dashboard
                    </button>
                    <button onClick={handleLogout} className="logout-btn">
                        Logout
                    </button>
                </div>
            </header>

            <nav className="dashboard-nav">
                <button
                    onClick={() => navigate('/')}
                    className="nav-btn"
                >
                    ID Scanners
                </button>
                <button
                    onClick={() => navigate('/webhooks')}
                    className="nav-btn"
                >
                    My Webhooks
                </button>
                <button
                    onClick={() => navigate('/security-policies')}
                    className="nav-btn active"
                >
                    Security Policies
                </button>
                <button
                    onClick={() => navigate('/operational-log')}
                    className="nav-btn"
                >
                    Operational Log
                </button>
            </nav>

            <div className="page-content">
                <div className="page-header">
                    <h2>Security Policies</h2>
                    <p>Define security validation rules and requirements for scanner operations</p>
                </div>

                <div className="page-actions">
                    <button onClick={openCreateModal} className="primary-btn">
                        + Create Policy
                    </button>
                </div>

                {error && !showModal && <div className="error">{error}</div>}
                {success && !showModal && <div className="success">{success}</div>}

                {loading ? (
                    <div className="loading">Loading security policies...</div>
                ) : policies.length === 0 ? (
                    <div className="empty-state">
                        <p>No security policies found. Create your first policy to get started.</p>
                    </div>
                ) : (
                    <div className="policies-grid">
                        {policies.map((policy) => (
                            <div key={policy.id} className="policy-card">
                                <div className="policy-card-header">
                                    <h3>{policy.name}</h3>
                                    {policy.requireDoubleSideScan && (
                                        <span className="double-side-badge">Double Side Required</span>
                                    )}
                                </div>

                                <div className="policy-card-body">
                                    <div className="info-row">
                                        <span className="label">Min Absolute Checks:</span>
                                        <span className="value">
                                            {policy.minimumRequiredPositiveSecurityChecksAbsolute}
                                        </span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Min Relative Checks:</span>
                                        <span className="value">
                                            {policy.minimumRequiredPositiveSecurityChecksRelative}%
                                        </span>
                                    </div>

                                    <div className="info-row">
                                        <span className="label">Double Side Scan:</span>
                                        <span className={`value ${policy.requireDoubleSideScan ? 'enabled' : 'disabled'}`}>
                                            {policy.requireDoubleSideScan ? 'Required' : 'Not Required'}
                                        </span>
                                    </div>

                                    {policy.usageCount !== undefined && (
                                        <div className="info-row">
                                            <span className="label">Used by:</span>
                                            <span className="value">{policy.usageCount} scanner(s)</span>
                                        </div>
                                    )}
                                </div>

                                <div className="policy-card-footer">
                                    <button
                                        onClick={() => openEditModal(policy)}
                                        className="edit-btn"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(policy)}
                                        className="delete-btn"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingPolicy ? 'Edit Security Policy' : 'Create Security Policy'}</h2>
                            <button className="close-btn" onClick={closeModal}>×</button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {error && <div className="error">{error}</div>}
                            {success && <div className="success">{success}</div>}

                            <div className="form-group">
                                <label>Policy Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="High Security Policy"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Minimum Absolute Checks *</label>
                                <input
                                    type="number"
                                    value={formData.minimumRequiredPositiveSecurityChecksAbsolute}
                                    onChange={(e) => handleInputChange('minimumRequiredPositiveSecurityChecksAbsolute', parseInt(e.target.value))}
                                    min="0"
                                    required
                                />
                                <small>Minimum number of positive security checks required (0 or greater)</small>
                            </div>

                            <div className="form-group">
                                <label>Minimum Relative Checks (%) *</label>
                                <input
                                    type="number"
                                    value={formData.minimumRequiredPositiveSecurityChecksRelative}
                                    onChange={(e) => handleInputChange('minimumRequiredPositiveSecurityChecksRelative', parseFloat(e.target.value))}
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    required
                                />
                                <small>Minimum percentage of positive security checks required (0-100)</small>
                            </div>

                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.requireDoubleSideScan}
                                        onChange={(e) => handleInputChange('requireDoubleSideScan', e.target.checked)}
                                    />
                                    <span>Require Double-Side Scan</span>
                                </label>
                                <small>When enabled, scanners using this policy must scan both sides of documents</small>
                            </div>

                            <div className="modal-footer">
                                <button type="submit" className="primary-btn">
                                    {editingPolicy ? 'Update Policy' : 'Create Policy'}
                                </button>
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={closeModal}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SecurityPolicies;
